import { gql } from 'graphql-request';
import { FETool } from '../components/AiChat/types';
import {
  ApiAgentContext,
  FetchAccountApi,
  SessionHistoryResponse,
  SessionStatus,
  UserSessionUsage,
} from '../types';
import { getBrowserTimezone } from '../utils/datetime';
import { patch, post, get } from '../utils/http';

export const setIsCriticalAssets = ({
  ids,
  isCritical,
  envId,
}: {
  ids: string[];
  isCritical: boolean;
  envId: string;
}) =>
  patch(`${window.VITE_API_BASE_URL}/${envId}/assets/`, {
    asset_ids: ids,
    is_critical: isCritical,
  });

export const sendTrackingEvent = (
  data: { eventName: string; properties: any },
  envId: string,
) => post(`${window.VITE_API_BASE_URL}/${envId}/events`, data);

export const reportViewMetric = (
  data: { view_name: string; duration: number },
  envId: string,
) => post(`${window.VITE_API_BASE_URL}/${envId}/view-metrics`, data);

export const fetchAccount = (): Promise<FetchAccountApi> =>
  get(`${window.VITE_API_BASE_URL}/account/`);

export type ByokSettings = {
  configured: boolean;
  provider?: 'anthropic' | 'openai' | null;
  model?: string | null;
  allowedModels?: string[];
  keyLast4?: string | null;
};

export const fetchByokSettings = (): Promise<ByokSettings> =>
  get(`${window.VITE_API_BASE_URL}/account/settings/ai/byok`);

export const fetchByokModels = (): Promise<{ models: string[] }> =>
  get(`${window.VITE_API_BASE_URL}/account/settings/ai/byok/models`);

export type DbtCloudConnection = {
  id: string;
  name: string;
  hostUrl: string;
  dbtCloudProdEnvironmentId?: string | null;
  tokenLast4?: string | null;
};

export type DbtCloudConnections = {
  connections: DbtCloudConnection[];
};

export const fetchDbtCloudConnections = (): Promise<DbtCloudConnections> =>
  get(`${window.VITE_API_BASE_URL}/account/settings/integrations/dbt-cloud`);

export const fetchAiQuota = (): Promise<UserSessionUsage | null> => {
  const tz = getBrowserTimezone();
  return get(`${window.VITE_API_BASE_URL}/ai-chat/quota`, undefined, {
    headers: tz ? { 'X-User-Timezone': tz } : undefined,
  });
};

const userTimezoneHeaders = (): Record<string, string> | undefined => {
  const tz = getBrowserTimezone();
  return tz ? { 'X-User-Timezone': tz } : undefined;
};

export const createSession = (body: {
  prompt: string;
  mode?: string;
  model?: string;
  context?: ApiAgentContext;
  environment_id?: string;
  trigger_origin?: string;
  enabled_integrations?: string[];
  tools?: FETool[];
}): Promise<{ session_id: string }> =>
  post(`${window.VITE_API_BASE_URL}/ai/sessions`, body, {
    headers: userTimezoneHeaders(),
  });

export const fetchChatIntegrations = (): Promise<{
  integrations: string[];
  fivetran_webhook_only: boolean;
}> => get(`${window.VITE_API_BASE_URL}/ai/sessions/integrations`);

export const sendSessionMessage = (
  sessionId: string,
  body: {
    prompt: string;
    mode?: string;
    model?: string;
    context?: ApiAgentContext;
    enabled_integrations?: string[];
    tools?: FETool[];
  },
): Promise<void> =>
  post(`${window.VITE_API_BASE_URL}/ai/sessions/${sessionId}/messages`, body, {
    headers: userTimezoneHeaders(),
  });

export const respondToSessionTool = (
  sessionId: string,
  body: {
    kind: 'approval' | 'output';
    tool_call_id: string;
    approved?: boolean;
    args_override?: Record<string, unknown>;
    value?: unknown;
  },
): Promise<void> =>
  post(
    `${window.VITE_API_BASE_URL}/ai/sessions/${sessionId}/tool-response`,
    body,
  );

export const cancelSession = (sessionId: string): Promise<void> =>
  post(`${window.VITE_API_BASE_URL}/ai/sessions/${sessionId}/cancel`);

type UpdateSessionBody = {
  title?: string;
  pinned?: boolean;
};

export const updateSession = (
  sessionId: string,
  body: UpdateSessionBody,
): Promise<void> =>
  patch(`${window.VITE_API_BASE_URL}/ai/sessions/${sessionId}`, body);

export const updateSessionTitle = (
  sessionId: string,
  title: string,
): Promise<void> => updateSession(sessionId, { title });

export const fetchSessionHistory = (
  limit: number = 50,
  offset: number = 0,
  pinned?: boolean,
): Promise<SessionHistoryResponse> =>
  get(`${window.VITE_API_BASE_URL}/ai/sessions`, {
    limit,
    offset,
    ...(pinned !== undefined && { pinned }),
  });

export const buildSessionEventsUrl = (
  sessionId: string,
  fromSeq: number = -1,
): string =>
  `${window.VITE_API_BASE_URL}/ai/sessions/${sessionId}/events?from_seq=${fromSeq}`;

export const fetchSessionEvents = (
  sessionId: string,
  fromSeq: number = -1,
): Promise<{ status: SessionStatus | null; events: Record<string, any>[] }> =>
  get(`${window.VITE_API_BASE_URL}/ai/sessions/${sessionId}/events/history`, {
    from_seq: fromSeq,
  });

export const getTreeViewQuery = gql`
  query GetTreeView(
    $category: TreeCategory!
    $view: TreeViewType!
    $filters: [Filter!]
    $includeTotals: Boolean!
  ) {
    treeView(
      category: $category
      view: $view
      filters: $filters
      includeTotals: $includeTotals
      includeInternalAssets: false
    ) {
      name
      data
    }
  }
`;

export const getTreeStatusTotalsQuery = gql`
  query GetTreeStatusTotals(
    $category: TreeCategory!
    $view: TreeViewType!
    $daysBack: Int!
    $items: [TreeStatusTotalsItemInput!]!
    $filters: [Filter!]
    $excludeResourceTypes: [String!]
  ) {
    treeStatusTotals(
      category: $category
      view: $view
      daysBack: $daysBack
      items: $items
      filters: $filters
      excludeResourceTypes: $excludeResourceTypes
      includeInternalAssets: false
    ) {
      requestId
      totals {
        passed
        warnings
        failures
        errors
      }
    }
  }
`;

export const getTreeIncidentStatusTotalsQuery = gql`
  query GetTreeIncidentStatusTotals(
    $category: TreeCategory!
    $view: TreeViewType!
    $daysBack: Int!
    $items: [TreeStatusTotalsItemInput!]!
    $filters: [Filter!]
    $excludeResourceTypes: [String!]
  ) {
    treeIncidentStatusTotals(
      category: $category
      view: $view
      daysBack: $daysBack
      items: $items
      filters: $filters
      excludeResourceTypes: $excludeResourceTypes
      includeInternalAssets: false
    ) {
      requestId
      statusCounts {
        open
        acknowledged
        resolved
      }
    }
  }
`;
